import React, { useState, useEffect, useMemo, useCallback, ChangeEvent } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import { getWorkflowTranscript, deleteCalls } from 'services/api/WorkflowServiceAPI';
import { useExportCallsCSV } from 'services/hooks/mutations';
import { useGetAgenticWorkflowDetails, useGetCallsData } from 'services/hooks/queries';
import { BulkActions, CallDetailsModal, CallsTable, SelectAllBanner } from '../components';
import {
    CallAnalyticsFilters,
    CallStatusSummary,
    CallAnalyticsActiveFilterChip,
    getStatusGroupFilterValue,
} from '../components/CallAnalytics';
import type { CallAnalyticsMixFilter } from '../components/CallAnalytics';
import { Pagination } from 'common/components';
import { EXPORT_MAX_RECORDS } from 'common/constants';
import { SortDirection, SortField } from 'types/call.type';
import type {
    AgenticWorkflowOutletContext,
    DateRangeType,
    HandleDeleteCallsParams,
    HandleExportCSVParams,
    Lead,
} from '../AgenticWorkflowDetail.types';
import {
    downloadCsv,
    formatCallOutcome,
    formatDisposition,
    formatDuration,
    formatDurationTooltip,
    formatLeadStatus,
    getDateRangeForRangeType,
    setDateTimes,
} from '../utils';
import type { CallsTableFilterName } from '../components/CallsTable/CallsTable';

import styles from '../AgenticWorkflowDetail.module.scss';

const CallAnalyticsPageNew: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const {
        initialStartDate,
        initialEndDate,
        humanTransfer,
        refetchWorkflowDetails,
        openNotification,
        closeNotification,
    } = useOutletContext<AgenticWorkflowOutletContext>();

    const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([initialStartDate, initialEndDate]);
    const [dateRangeType, setDateRangeType] = useState<DateRangeType>('today');
    const [filterLeadStatus, setFilterLeadStatus] = useState('');
    const [filterCallStatus, setFilterCallStatus] = useState('');
    const [filterCallDirection, setFilterCallDirection] = useState('');
    const [filterDisposition, setFilterDisposition] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [sortField, setSortField] = useState<SortField>('callDate');
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
    const [selectedCalls, setSelectedCalls] = useState<string[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [selectedCall, setSelectedCall] = useState<Lead | null>(null);
    const [showCallDetailsModal, setShowCallDetailsModal] = useState(false);
    const [mixFilter, setMixFilter] = useState<CallAnalyticsMixFilter | null>(null);
    const [isAllPagesSelected, setIsAllPagesSelected] = useState(false);

    const { exportCalls, isExporting } = useExportCallsCSV();

    const resetAllPagesSelected = useCallback(() => setIsAllPagesSelected(false), []);

    const handleSetDateRange = useCallback(
        (dates: [Date | null, Date | null]) => {
            setDateRange(dates);
            setCurrentPage(1);
            resetAllPagesSelected();
        },
        [resetAllPagesSelected]
    );
    const { startWithTime, endWithTime } = setDateTimes(dateRange);

    const sharedFilters = useMemo(
        () => ({
            startDate: startWithTime,
            endDate: endWithTime,
            callStatus: filterCallStatus,
            partyStage: filterLeadStatus,
            callDirection: filterCallDirection,
            disposition: filterDisposition,
            search: searchTerm,
            combinedOverdue: dateRangeType === 'today',
        }),
        [
            startWithTime,
            endWithTime,
            filterCallStatus,
            filterLeadStatus,
            filterCallDirection,
            filterDisposition,
            searchTerm,
            dateRangeType,
        ]
    );

    const statisticQueryParams = useMemo(
        () => ({
            id: id || '',
            startDate: sharedFilters.startDate,
            endDate: sharedFilters.endDate,
            callStatus: sharedFilters.callStatus,
            partyStage: sharedFilters.partyStage,
            callDirection: sharedFilters.callDirection,
            disposition: sharedFilters.disposition,
            search: sharedFilters.search,
            currentPage,
            perPage: rowsPerPage,
            isRefetchEnabled: false,
        }),
        [id, sharedFilters, currentPage, rowsPerPage]
    );

    const callsQueryParams = useMemo(
        () => ({
            id: id || '',
            startDate: sharedFilters.startDate,
            endDate: sharedFilters.endDate,
            callStatus: sharedFilters.callStatus,
            partyStage: sharedFilters.partyStage,
            callDirection: sharedFilters.callDirection,
            disposition: sharedFilters.disposition,
            search: sharedFilters.search,
            combinedOverdue: sharedFilters.combinedOverdue,
            perPage: rowsPerPage,
            sortOrder: sortDirection,
            sortBy: sortField,
            currentPage,
        }),
        [id, sharedFilters, rowsPerPage, sortDirection, sortField, currentPage]
    );

    const { agenticWorkflowDetails: workflowDetails } = useGetAgenticWorkflowDetails(statisticQueryParams);
    const { callsData } = useGetCallsData(callsQueryParams, true, false);

    const leads = useMemo<Lead[]>(() => {
        if (!callsData?.data?.length) return [];
        return callsData.data.map((call) => {
            const callDateTime = new Date(call.startTime || call.scheduledDate);
            const durationSeconds = call.callDuration || 0;
            return {
                id: call.crmId,
                callId: call.callId,
                callDate: callDateTime.toLocaleDateString('en-US'),
                callTime: callDateTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
                outcome: call.status || 'PENDING',
                duration: formatDuration(durationSeconds),
                durationTooltip: formatDurationTooltip(durationSeconds),
                crmStatus: call.partyStage || 'NEW_LEAD',
                crmId: call.crmId,
                name: call.contactName || 'Unknown',
                number: call.phoneNumber || 'Unknown',
                transferTo: call.transferTo || '',
                disposition: call.disposition || '',
                callDirection: call.callDirection,
                action: '',
                recording: call.callId,
                transcript: call.callId,
                aggregatedTagsUsed: call.aggregatedTagsUsed,
            };
        });
    }, [callsData]);

    const totalPages = callsData?.pagination?.totalPages || 1;
    const totalRecords = callsData?.pagination?.totalRecords || 0;
    const currentPageFromAPI = callsData?.pagination?.currentPage || currentPage;
    const perPageFromAPI = callsData?.pagination?.perPage || rowsPerPage;

    useEffect(() => {
        const paginationData = callsData?.pagination;
        if (paginationData?.currentPage) setCurrentPage(paginationData.currentPage);
        if (paginationData?.perPage) setRowsPerPage(paginationData.perPage);
    }, [callsData?.pagination]);

    useEffect(() => {
        if (totalPages > 0 && currentPage > totalPages) setCurrentPage(totalPages);
    }, [totalPages, currentPage]);

    const handleSortChange = (field: SortField, newSortDirection: SortDirection) => {
        setSortField(field);
        setSortDirection(newSortDirection);
    };

    const handlePageChange = (page: number) => setCurrentPage(page);

    const handleRowsPerPageChange = (newRowsPerPage: number) => {
        setRowsPerPage(newRowsPerPage);
        setCurrentPage(1);
    };

    const handleClearFilters = () => {
        setSearchTerm('');
        setFilterLeadStatus('');
        setFilterCallStatus('');
        setFilterCallDirection('');
        setFilterDisposition('');
        setMixFilter(null);
        setDateRangeType('today');
        setDateRange([new Date(), new Date()]);
        setCurrentPage(1);
        setSelectedCalls([]);
        resetAllPagesSelected();
    };

    const handleMixFilterChange = useCallback(
        (filter: CallAnalyticsMixFilter | null) => {
            setMixFilter(filter);
            if (!filter) {
                setFilterCallStatus('');
                setFilterDisposition('');
            } else if (filter.type === 'status') {
                setFilterDisposition('');
                setFilterCallStatus(getStatusGroupFilterValue(filter.key));
            } else {
                setFilterCallStatus('');
                setFilterDisposition(filter.key);
            }
            setCurrentPage(1);
            setSelectedCalls([]);
            resetAllPagesSelected();
        },
        [resetAllPagesSelected]
    );

    const handleSearchChange = (value: string) => {
        if (value.length > 1 || value.length === 0) {
            setSearchTerm(value);
            setCurrentPage(1);
            resetAllPagesSelected();
        }
    };

    const handleFilterChange = (filter: CallsTableFilterName, value: string) => {
        if (filter === 'callStatus' || filter === 'disposition') {
            setMixFilter(null);
        }
        if (filter === 'leadStatus') setFilterLeadStatus(value);
        else if (filter === 'callStatus') setFilterCallStatus(value);
        else if (filter === 'callDirection') setFilterCallDirection(value);
        else setFilterDisposition(value);
        setCurrentPage(1);
        resetAllPagesSelected();
    };

    const handleDateRangeTypeChange = (range: string) => {
        const newRange = getDateRangeForRangeType(range);
        setDateRangeType(range as DateRangeType);
        if (newRange) {
            setCurrentPage(1);
            setDateRange(newRange);
        }
        resetAllPagesSelected();
    };

    const handleCheckboxChange = (callId: string) => {
        setSelectedCalls((prev) =>
            prev.includes(callId) ? prev.filter((item) => item !== callId) : [...prev, callId]
        );
        resetAllPagesSelected();
    };

    const handleSelectAllCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
        event.stopPropagation();
        if (event.target.checked) {
            setSelectedCalls(leads.map((lead) => lead.callId));
        } else {
            const pageIds = leads.map((lead) => lead.callId);
            setSelectedCalls(selectedCalls.filter((callId) => !pageIds.includes(callId)));
            resetAllPagesSelected();
        }
    };

    const handleDeleteCalls = async ({ selectedCallId }: HandleDeleteCallsParams) => {
        if (!selectedCallId && !selectedCalls.length) {
            openNotification({
                isOpen: true,
                title: 'Selection Required',
                message: 'Please select at least one call to delete.',
                type: 'error',
                body: null,
                buttons: [],
            });
            return;
        }
        openNotification({
            isOpen: true,
            title: 'Confirm Deletion',
            message: `Are you sure you want to delete ${selectedCallId ? 'this call' : `${selectedCalls.length} call(s)`}?`,
            type: 'error',
            body: null,
            buttons: [
                { label: 'Cancel', onClick: closeNotification, style: { backgroundColor: '#9b9b9b' } },
                {
                    label: 'Delete',
                    onClick: async () => {
                        closeNotification();
                        try {
                            const idsToDelete = selectedCallId ? [selectedCallId] : selectedCalls;
                            await deleteCalls(idsToDelete);
                            openNotification({
                                isOpen: true,
                                title: 'Deletion Successful',
                                message: 'Calls deleted successfully',
                                type: 'success',
                                body: null,
                                buttons: [],
                            });
                            await refetchWorkflowDetails();
                            setSelectedCalls([]);
                        } catch (err) {
                            console.error('Error deleting calls:', err);
                            openNotification({
                                isOpen: true,
                                title: 'Error',
                                message: `Failed to delete calls: ${(err as Error).message || 'Unknown error'}`,
                                type: 'error',
                                body: null,
                                buttons: [],
                            });
                        }
                    },
                    style: { backgroundColor: '#f44336' },
                },
            ],
        });
    };

    const runBackendExport = (includeTranscripts: boolean) => {
        openNotification({
            isOpen: true,
            title: 'Exporting',
            message: `Exporting${totalRecords > EXPORT_MAX_RECORDS ? ` ${EXPORT_MAX_RECORDS.toLocaleString()} of ${totalRecords.toLocaleString()}` : ` ${totalRecords.toLocaleString()}`} calls...`,
            type: 'success',
            body: null,
            buttons: [],
        });

        exportCalls(
            {
                id: id || '',
                startDate: startWithTime,
                endDate: endWithTime,
                callStatus: filterCallStatus,
                partyStage: filterLeadStatus,
                callDirection: filterCallDirection,
                disposition: filterDisposition,
                search: searchTerm,
                sortOrder: sortDirection,
                sortBy: sortField,
                includeTranscripts,
                combinedOverdue: dateRangeType === 'today',
            },
            {
                onSuccess: () => {
                    openNotification({
                        isOpen: true,
                        title: 'Export Complete',
                        message: 'CSV file downloaded successfully.',
                        type: 'success',
                        body: null,
                        buttons: [],
                    });
                    setSelectedCalls([]);
                    resetAllPagesSelected();
                },
                onError: () => {
                    openNotification({
                        isOpen: true,
                        title: 'Export Failed',
                        message: 'Failed to export CSV. Please try again.',
                        type: 'error',
                        body: null,
                        buttons: [],
                    });
                },
            }
        );
    };

    const showExportDialog = (onConfirm: (includeTranscripts: boolean) => void) => {
        let includeTranscripts = true;

        const capWarning =
            totalRecords > EXPORT_MAX_RECORDS
                ? `Your filters match ${totalRecords.toLocaleString()} calls. Exports are limited to ${EXPORT_MAX_RECORDS.toLocaleString()} calls. The most recent ${EXPORT_MAX_RECORDS.toLocaleString()} calls will be exported. Narrow your date range or filters for a complete export.`
                : null;

        openNotification({
            isOpen: true,
            title: 'Export Calls',
            message: capWarning || `Export ${totalRecords.toLocaleString()} calls to CSV?`,
            type: capWarning ? 'error' : 'success',
            body: (
                <label
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        marginTop: '0.5rem',
                        cursor: 'pointer',
                    }}
                >
                    <input
                        type='checkbox'
                        defaultChecked={true}
                        onChange={(e) => {
                            includeTranscripts = e.target.checked;
                        }}
                    />
                    Include transcripts
                </label>
            ),
            buttons: [
                { label: 'Cancel', onClick: closeNotification, style: { backgroundColor: '#9b9b9b' } },
                {
                    label: 'Export',
                    onClick: () => {
                        closeNotification();
                        onConfirm(includeTranscripts);
                    },
                },
            ],
        });
    };

    const handleExportAllCSV = () => {
        showExportDialog((includeTranscripts) => runBackendExport(includeTranscripts));
    };

    const handleExportCSV = async ({ selectedCallId }: HandleExportCSVParams) => {
        if (isAllPagesSelected) {
            showExportDialog((includeTranscripts) => runBackendExport(includeTranscripts));
            return;
        }

        if (!selectedCalls.length && !selectedCallId) {
            openNotification({
                isOpen: true,
                title: 'Selection Required',
                message: 'Please select at least one call to export.',
                type: 'error',
                body: null,
                buttons: [],
            });
            return;
        }
        try {
            openNotification({
                isOpen: true,
                title: 'Processing',
                message: 'Preparing CSV export including transcripts...',
                type: 'success',
                body: null,
                buttons: [],
            });
            const selectedLeads = leads.filter((lead) =>
                selectedCallId ? lead.callId === selectedCallId : selectedCalls.includes(lead.callId)
            );
            const headers = [
                'Call ID',
                'Call Date',
                'Call Time',
                'Call Status',
                'Duration',
                'Party Status',
                'Contact ID',
                'Name',
                'Number',
                'Transfer To',
                'Disposition',
                'Transcript',
            ];
            const csvRows: string[][] = [];
            for (const lead of selectedLeads) {
                let transcript = '';
                if (lead.transcript) {
                    try {
                        const transcriptData = await getWorkflowTranscript(lead.callId);
                        if (transcriptData?.speeches && Array.isArray(transcriptData.speeches)) {
                            transcript = transcriptData.speeches
                                .map(
                                    (e: { speaker: string; text: string }) =>
                                        `${e.speaker === 'assistant' ? 'SLAI Agent' : lead.name || 'Customer'}: ${e.text}`
                                )
                                .join('\n');
                        } else if (transcriptData?.transcript) {
                            transcript = transcriptData.transcript;
                        }
                    } catch {
                        // ignore
                    }
                }
                csvRows.push([
                    lead.callId,
                    lead.callDate,
                    lead.callTime,
                    formatCallOutcome(lead.outcome),
                    lead.duration,
                    formatLeadStatus(lead.crmStatus),
                    lead.id,
                    lead.name,
                    lead.number,
                    lead.transferTo,
                    formatDisposition(lead.disposition),
                    transcript,
                ]);
            }
            const dateString = new Date().toISOString().split('T')[0];
            downloadCsv(headers, csvRows, `workflow_${dateString}.csv`);
            openNotification({
                isOpen: true,
                title: 'Export Complete',
                message: `Successfully exported ${selectedLeads.length} call(s) to CSV.`,
                type: 'success',
                body: null,
                buttons: [],
            });
            setSelectedCalls([]);
        } catch (err) {
            console.error('Error exporting CSV:', err);
            openNotification({
                isOpen: true,
                title: 'Export Failed',
                message: 'Failed to export CSV. Please try again.',
                type: 'error',
                body: null,
                buttons: [],
            });
        }
    };

    const handleCallDetails = (lead: Lead) => {
        setSelectedCall(lead);
        setShowCallDetailsModal(true);
    };

    const onCloseCallDetailsModal = () => {
        setShowCallDetailsModal(false);
        setSelectedCall(null);
    };

    const allPageCallsSelected = leads.length > 0 && leads.every((l) => selectedCalls.includes(l.callId));
    const showSelectAllBanner = (allPageCallsSelected || isAllPagesSelected) && totalPages > 1;

    const isWorkflowLoading = !workflowDetails;

    return (
        <div className='flex flex-col gap-2xl' data-testid='call-analytics-page-new'>
            <CallAnalyticsFilters
                startDate={dateRange[0]}
                endDate={dateRange[1]}
                filterCallStatus={filterCallStatus}
                filterLeadStatus={filterLeadStatus}
                filterCallDirection={filterCallDirection}
                filterDisposition={filterDisposition}
                partyStages={workflowDetails?.partyStages ?? []}
                onSearchChange={handleSearchChange}
                onDateRangeTypeChange={handleDateRangeTypeChange}
                onDateRangeChange={handleSetDateRange}
                onFilterChange={handleFilterChange}
                onClear={handleClearFilters}
            />

            <CallStatusSummary
                callStats={workflowDetails?.callStatistic}
                humanTransfer={humanTransfer}
                activeFilter={mixFilter}
                onFilterChange={handleMixFilterChange}
            />

            <div>
                {mixFilter ? (
                    <CallAnalyticsActiveFilterChip
                        label={mixFilter.label}
                        onClear={() => handleMixFilterChange(null)}
                    />
                ) : null}
                <CallsTable
                    isLoading={isWorkflowLoading}
                    leads={leads}
                    selectedCalls={selectedCalls}
                    selectedCall={selectedCall}
                    sortField={sortField}
                    sortDirection={sortDirection}
                    searchTerm={searchTerm}
                    filterLeadStatus={filterLeadStatus}
                    filterCallStatus={filterCallStatus}
                    filterCallDirection={filterCallDirection}
                    filterDisposition={filterDisposition}
                    partyStages={workflowDetails?.partyStages ?? []}
                    onSortChange={handleSortChange}
                    onFilterChange={handleFilterChange}
                    onSelectAllCheckboxChange={handleSelectAllCheckboxChange}
                    onCheckboxChange={handleCheckboxChange}
                    onCallDetails={handleCallDetails}
                    showColumnFilters={false}
                    dateRangeConfig={{
                        type: dateRangeType,
                        onTypeChange: handleDateRangeTypeChange,
                        setDateRange: handleSetDateRange,
                    }}
                />
                {showSelectAllBanner && (
                    <SelectAllBanner
                        perPage={leads.length}
                        totalRecords={totalRecords}
                        isAllPagesSelected={isAllPagesSelected}
                        onSelectAllPages={() => setIsAllPagesSelected(true)}
                        onClearSelection={() => {
                            setIsAllPagesSelected(false);
                            setSelectedCalls([]);
                        }}
                    />
                )}
                <div className='flex justify-between items-center flex-wrap gap-sm'>
                    <BulkActions
                        selectedCalls={selectedCalls}
                        handleDeleteCalls={handleDeleteCalls}
                        handleExportCSV={handleExportCSV}
                        totalRecords={totalRecords}
                        isAllPagesSelected={isAllPagesSelected}
                        onExportAll={handleExportAllCSV}
                        isExporting={isExporting}
                    />
                    <Pagination
                        currentPage={currentPageFromAPI}
                        totalPages={totalPages}
                        rowsPerPage={perPageFromAPI}
                        onPageChange={handlePageChange}
                        onRowsPerPageChange={handleRowsPerPageChange}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        className={styles.agenticPagination}
                    />
                </div>
            </div>
            <CallDetailsModal
                isOpen={showCallDetailsModal}
                selectedCall={selectedCall}
                setSelectedCall={setSelectedCall}
                leads={leads}
                onClose={onCloseCallDetailsModal}
                handleDeleteCall={handleDeleteCalls}
                handleExportCSV={handleExportCSV}
                currentPage={currentPage}
                handlePageChange={handlePageChange}
                totalPages={totalPages}
                isLoading={isWorkflowLoading}
            />
        </div>
    );
};

export default CallAnalyticsPageNew;
